import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Activity, ShieldCheck, GitBranch } from "lucide-react";
import cubeLogo from "../assets/tm-cube.png";
import { API } from "../lib/api";

const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    fetch(`${API}/branding/public`).then((r) => r.ok ? r.json() : null).then(setBrand).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const productName = brand?.product_name || "Task Matrix";
  const tagline = brand?.tagline || "Tasks. Ownership. Execution.";
  const subtitle = brand?.login_subtitle || "Enter your credentials to continue";
  const bg = brand?.login_background || "#0B1120";
  const btnColor = brand?.login_button_color;
  const logoUrl = absUrl(brand?.logo_url) || cubeLogo;
  const panelRight = (brand?.login_panel_placement || "right") === "right";
  const centerContent = brand?.login_alignment === "center";
  const texts = brand?.texts || {};
  const styles = brand?.styles || {};
  const fontFamily = styles.font_family || undefined;

  const defaultFeatures = [
    { title: "Clarity", desc: "One source of truth for every task" },
    { title: "Accountability", desc: "Clear owners on every commitment" },
    { title: "Visibility", desc: "Dependencies & status at a glance" },
  ];
  const featureTexts = (texts.features && texts.features.length ? texts.features : defaultFeatures);
  const featureIcons = [Activity, ShieldCheck, GitBranch];
  const features = featureTexts.map((f, i) => ({ icon: featureIcons[i % 3], ...f }));
  const oauthLabel = (p) => (texts.oauth_button_text || "Continue with {provider}").replace("{provider}", p === "oidc" ? "SSO" : p);

  const BrandPanel = (
    <div className="hidden lg:flex flex-col justify-between w-[52%] text-white px-16 py-12" style={{ background: bg, fontFamily }}>
      <div className={`flex items-center gap-4 ${centerContent ? "justify-center" : ""}`}>
        <img src={logoUrl} alt={productName} className="h-14 w-auto" data-testid="login-logo" />
        <div className="border-l border-slate-700 pl-4">
          <div className="text-base font-semibold tracking-wide" style={styles.brand_name_color ? { color: styles.brand_name_color } : undefined}>{productName}</div>
          <div className="text-xs tracking-wide" style={{ color: styles.tagline_color || "#22D3EE" }}>{tagline}</div>
        </div>
      </div>
      <div className={`max-w-lg ${centerContent ? "text-center mx-auto" : ""}`}>
        <h1 className="font-bold tracking-tight" style={{ fontSize: `${styles.title_size || 44}px`, fontWeight: styles.title_weight || 700, lineHeight: styles.title_line_height || 1.15, color: styles.title_color || "#FFFFFF" }}>
          {texts.main_title || "Organizational Execution Operating System"}
        </h1>
        <p className="mt-6" style={{ fontSize: `${styles.desc_size || 18}px`, lineHeight: styles.desc_line_height || 1.6, color: styles.desc_color || "#94A3B8" }}>
          {texts.description || "A single source of truth for tasks, ownership, dependencies and accountability across every department."}
        </p>
        <div className="mt-12 grid grid-cols-3 gap-4">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 hover:border-slate-600 transition-colors">
              <Icon size={20} className="text-cyan-400 mb-3" />
              <div className="font-semibold text-sm">{title}</div>
              <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 tracking-wide">{texts.footer_text || "Exception-driven review · RBAC · Full audit trail"}</div>
        <div className="text-xs text-slate-600 mt-2 tracking-wide" data-testid="login-footer-brand">
          ✦&nbsp;&nbsp;{texts.powered_by_text || "Powered by Mind Compute"}&nbsp;&nbsp;|&nbsp;&nbsp;{texts.copyright_text || `© 2026 ${productName}. All Rights Reserved.`}
        </div>
      </div>
    </div>
  );

  const FormPanel = (
    <div className="flex-1 flex flex-col bg-white" style={{ fontFamily }}>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={logoUrl} alt={productName} className="h-16 w-auto mb-3" data-testid="login-logo-mobile" />
            <div className="text-xs text-slate-500 tracking-wide">{tagline}</div>
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: styles.signin_title_color || "#0F172A" }}>{texts.signin_title || "Sign in"}</h2>
            <p className="text-slate-500 text-sm mt-1.5">{subtitle}</p>
          </div>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="login-email">{texts.email_label || "Email"}</Label>
              <Input id="login-email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder={texts.email_placeholder || "you@company.com"} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password">{texts.password_label || "Password"}</Label>
              <Input id="login-password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder={texts.password_placeholder || "••••••••"} className="h-11" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold" style={btnColor ? { backgroundColor: btnColor } : undefined}>
              {loading ? "Signing in…" : (texts.signin_button_text || "Sign in")}
            </Button>
            {brand?.oauth_providers?.length > 0 && (
              <div className="space-y-2 pt-1">
                {brand.oauth_providers.map((p) => (
                  <button key={p} type="button" className="w-full h-10 rounded-md border text-sm text-slate-600 hover:bg-slate-50" data-testid={`oauth-btn-${p}`}>{oauthLabel(p)}</button>
                ))}
              </div>
            )}
            <div className="text-center pt-1">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 hover:underline">{texts.forgot_password_text || "Forgot password?"}</Link>
            </div>
            <div className="text-center border-t border-slate-100 pt-4 mt-1 text-sm text-slate-500">
              New to {productName}? <Link to="/signup" data-testid="login-to-signup" className="text-blue-600 hover:text-blue-700 hover:underline font-medium">Create an account</Link>
            </div>
          </div>
        </form>
      </div>
      <div className="lg:hidden text-center pb-6 text-xs text-slate-400 tracking-wide" data-testid="login-footer-brand-mobile">
        ✦&nbsp;&nbsp;{texts.powered_by_text || "Powered by Mind Compute"}&nbsp;&nbsp;|&nbsp;&nbsp;{texts.copyright_text || `© 2026 ${productName}. All Rights Reserved.`}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {panelRight ? <>{BrandPanel}{FormPanel}</> : <>{FormPanel}{BrandPanel}</>}
    </div>
  );
}
